import { useState } from 'react';
import { ApiError, claim, getUnclaimed, logout } from '../core/api';
import { useAsync } from '../core/useAsync';
import { useT } from '../core/lexicon';
import { Avatar } from '../core/sprite';
import { Screen } from './Layout';

/**
 * The claim gate: a signed-in but unclaimed user picks their roster profile and
 * enters the join code. Permanent (auth.claim.warning) — the API binds the AAD
 * identity to the chosen row. On success we re-read /api/me (onClaimed).
 */
export function Claim({ onClaimed }: { onClaimed: () => void }) {
  const t = useT();
  const roster = useAsync(getUnclaimed, []);
  const [code, setCode] = useState('');
  const [picked, setPicked] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  async function submit() {
    if (picked == null || !code.trim()) return;
    setBusy(true);
    setProblem(null);
    try {
      await claim(code.trim(), picked);
      onClaimed();
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 403) setProblem(t('auth.rejected'));
      else if (err.status === 409) {
        setProblem(err.message);
        roster.reload();
        setPicked(null);
      } else setProblem(err.message || 'Could not claim.');
      setBusy(false);
    }
  }

  return (
    <Screen>
      <header className="claim-head">
        <h1>{t('brand.name')}</h1>
        <p>{t('auth.claim.warning')}</p>
      </header>

      <div className="field-row">
        <label htmlFor="jc">{t('auth.join_code')}</label>
        <input
          id="jc"
          value={code}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="——————"
        />
      </div>

      <div className="warn">{t('auth.claim')}</div>

      <div className="list">
        {roster.loading ? (
          <div className="empty">
            <div className="spin">LOADING…</div>
          </div>
        ) : roster.error ? (
          <div className="empty">
            <p className="errline">{roster.error.message}</p>
          </div>
        ) : (roster.data ?? []).length === 0 ? (
          <div className="empty">
            <h3>NOBODY LEFT</h3>
            <p>Every profile is already claimed. Ask the admin to unbind yours.</p>
          </div>
        ) : (
          (roster.data ?? []).map((p) => (
            <button
              key={p.user_id}
              className={`claimrow${picked === p.user_id ? ' on' : ''}`}
              onClick={() => setPicked(p.user_id)}
            >
              <Avatar spriteData={p.sprite_data} seed={p.sprite_key || p.display_name} size={36} />
              <span>
                <span className="cn">{p.display_name}</span>
                {p.bio ? <span className="cb">{p.bio}</span> : null}
              </span>
              <span className="pick">▶</span>
            </button>
          ))
        )}
      </div>

      {problem ? <div className="errline" style={{ margin: '0 12px 8px' }}>{problem}</div> : null}

      <div className="act">
        <button className="go" disabled={picked == null || !code.trim() || busy} onClick={submit}>
          {busy ? '…' : t('auth.claim.cta')}
        </button>
        <div className="saved">
          <a style={{ color: 'var(--k1)', cursor: 'pointer' }} onClick={logout}>
            sign out
          </a>
        </div>
      </div>
    </Screen>
  );
}
